import React, { useState } from 'react';
import {
	FiUser,
	FiMail,
	FiPhone,
	FiCalendar,
	FiMapPin,
	FiDollarSign,
	FiFileText,
	FiLink,
	FiCheck,
	FiX,
	FiImage,
	FiFile,
	FiHash,
	FiType,
	FiEdit3,
	FiSave,
	FiXCircle,
	FiAlertCircle,
	FiDownload,
} from 'react-icons/fi';
import Label from '../../components/ui/label';
import Button from '../../components/ui/button';
import SublyApi from '../../helpers/Api';
import MapboxPlacesAutocomplete from '../../components/ui/mapbox-places-autocomplete';
import {
	parseFieldOptions,
	resolveOptionLabel,
} from '../../utils/formHelpers';

// DEBUG mode - set to true to visualize element boundaries and overflow
const DEBUG = false;

// Helper function to extract filename from URL (module-level utility)
const getFilenameFromUrl = (url) => {
	try {
		const urlObj = new URL(url);
		const pathname = urlObj.pathname;
		// Get the last part of the path
		const filename = pathname.split('/').pop();
		// Decode the filename (pathname already excludes query parameters)
		return decodeURIComponent(filename) || 'file';
	} catch (error) {
		return 'file';
	}
};

// Sanitize helper: remove special chars except alphanumeric, accented Latin
// characters, and hyphens; replace spaces with hyphens, trim leading/trailing hyphens
const sanitize = (str) => {
	if (!str) return '';
	str = String(str);
	return str
		.replace(/[^a-zA-Z0-9\u00C0-\u024F\s-]/g, '')
		.replace(/\s+/g, '-')
		.replace(/^-+|-+$/g, '');
};

// Helper function to build a smart download filename from submission data.
// Uses the user record (submission.first_name/last_name) for client name,
// dynamically detects address fields from form_fields, shortens addresses
// to street + city only, and caps the base filename at ~50 characters.
// Returns a filename like: Jane-Rothe_123-Main-St-Springfield_1.jpg
const buildDownloadFilename = (submission, formData, fileUrl, fileIndex) => {
	const fallbackFilename = getFilenameFromUrl(fileUrl);

	// Extract file extension from the original URL
	const extMatch = fallbackFilename.match(/\.([a-zA-Z0-9]+)$/);
	const ext = extMatch ? extMatch[1] : '';

	// Extract client name — always from user record
	let clientName = '';
	if (submission) {
		const firstName = (submission.first_name || '').trim();
		const lastName = (submission.last_name || '').trim();
		clientName = [firstName, lastName].filter(Boolean).join(' ');
	}
	const sanitizedName = sanitize(clientName);

	// Dynamically detect address field keys from form_fields
	const addressFieldKeys = [];
	if (submission?.form_fields && Array.isArray(submission.form_fields)) {
		submission.form_fields.forEach((field) => {
			if (field.type?.toLowerCase() === 'address' && field.field_key) {
				addressFieldKeys.push(field.field_key);
			}
		});
	}

	// Extract address (street + city only) from the first matching address field
	let address = '';
	if (formData) {
		for (const key of addressFieldKeys) {
			const val = formData[key];
			if (val) {
				if (typeof val === 'object' && val !== null) {
					const street = val.street || '';
					const city = val.city || '';
					const shortParts = [street, city].filter(Boolean);
					if (shortParts.length > 0) {
						address = shortParts.join(' ');
					} else if (val.formatted_address) {
						const faParts = val.formatted_address.split(',').map(p => p.trim());
						address = faParts.slice(0, 2).join(' ');
					}
				} else if (typeof val === 'string' && val.trim()) {
					const strParts = val.trim().split(',').map(p => p.trim());
					address = strParts.slice(0, 2).join(' ');
				}
				if (address) break;
			}
		}
	}
	const sanitizedAddress = sanitize(address);

	// If both are empty, fall back to original filename
	if (!sanitizedName && !sanitizedAddress) {
		return fallbackFilename;
	}

	// Build filename from non-empty parts
	const parts = [sanitizedName, sanitizedAddress, String(fileIndex)].filter(Boolean);
	let baseName = parts.join('_');

	// Cap filename length to avoid excessively long names
	const MAX_BASENAME_LENGTH = 50;
	if (baseName.length > MAX_BASENAME_LENGTH) {
		const indexPart = `_${fileIndex}`;
		const budget = MAX_BASENAME_LENGTH - indexPart.length;

		if (sanitizedName && sanitizedAddress) {
			const maxNameLen = Math.min(sanitizedName.length, 20);
			const truncatedName = sanitizedName.substring(0, maxNameLen).replace(/-+$/, '');
			const maxAddrLen = budget - truncatedName.length - 1; // -1 for underscore
			const truncatedAddr = maxAddrLen > 0
				? sanitizedAddress.substring(0, maxAddrLen).replace(/-+$/, '')
				: '';
			baseName = [truncatedName, truncatedAddr, String(fileIndex)].filter(Boolean).join('_');
		} else {
			const mainPart = sanitizedName || sanitizedAddress;
			const truncated = mainPart.substring(0, budget).replace(/-+$/, '');
			baseName = `${truncated}${indexPart}`;
		}
	}

	return ext ? `${baseName}.${ext}` : baseName;
};

// Helper function to download file from URL (handles cross-origin)
// Returns a promise that resolves to { success: boolean, fallback?: boolean }
const downloadFile = async (url, filename) => {
	try {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}
		const blob = await response.blob();
		const blobUrl = URL.createObjectURL(blob);

		const link = document.createElement('a');
		link.href = blobUrl;
		link.download = filename;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);

		URL.revokeObjectURL(blobUrl);
		return { success: true };
	} catch (error) {
		console.error('Error downloading file:', error);
		// Fallback: open in new tab
		window.open(url, '_blank');
		return { success: false, fallback: true };
	}
};

const FormResponsesDisplay = ({ submission, onDataUpdate, token }) => {
	// State for edit mode
	const [isEditMode, setIsEditMode] = useState(false);
	const [editingField, setEditingField] = useState(null);
	const [fieldValues, setFieldValues] = useState({});
	const [pendingChanges, setPendingChanges] = useState({});
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');

	// Helper function to detect file type from URL
	const getFileTypeFromUrl = (url) => {
		if (!url || typeof url !== 'string')
			return { isImage: false, isPdf: false, fileType: 'unknown' };

		const lowerUrl = url.toLowerCase();

		// Check for image extensions
		const imageExtensions = [
			'.jpg',
			'.jpeg',
			'.png',
			'.gif',
			'.webp',
			'.bmp',
			'.svg',
		];
		const isImage = imageExtensions.some((ext) => lowerUrl.includes(ext));

		// Check for PDF
		const isPdf = lowerUrl.includes('.pdf');

		// Determine file type
		let fileType = 'file';
		if (isImage) fileType = 'image';
		else if (isPdf) fileType = 'pdf';
		else if (lowerUrl.includes('.doc') || lowerUrl.includes('.docx'))
			fileType = 'document';
		else if (lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx'))
			fileType = 'spreadsheet';
		else if (lowerUrl.includes('.ppt') || lowerUrl.includes('.pptx'))
			fileType = 'presentation';

		return { isImage, isPdf, fileType };
	};

	// Wrapper to handle download with user feedback
	const handleDownload = async (url, filename) => {
		const result = await downloadFile(url, filename);
		if (result.fallback) {
			// Show a brief notification that download failed and file was opened in new tab
			setError('Download failed - file opened in a new tab instead.');
			// Clear the error after 3 seconds
			setTimeout(() => setError(''), 3000);
		}
	};

	// Parse form data
	let formData;
	try {
		formData =
			typeof submission?.data === 'string'
				? JSON.parse(submission.data)
				: submission?.data || {};
	} catch (e) {
		formData = {};
	}

	// After parsing formData, merge in file URLs for matching field keys
	// Merge file URLs into formData so that file/image previews appear in their respective fields
	if (submission?.files && Array.isArray(submission.files)) {
		submission.files.forEach((file) => {
			// Only set if missing OR value is not a usable URL
			const currentVal = formData[file.field_key];
			const isUsableUrl =
				typeof currentVal === 'string' && currentVal.startsWith('http');
			if (!isUsableUrl) {
				formData[file.field_key] = file.file_url;
			}
		});
	}

	// Determine files that do NOT correspond to a form field (or have been merged)
	const standaloneFiles = (submission?.files || []).filter(
		(file) => formData[file.field_key] !== file.file_url
	);

	// Build a map of file/image field_key -> 1-based order number for smart download naming
	const fileFieldOrderMap = {};
	if (submission?.form_fields) {
		let orderNum = 1;
		submission.form_fields.forEach((field) => {
			const fieldType = field.type?.toLowerCase();
			if (fieldType === 'file' || fieldType === 'image') {
				fileFieldOrderMap[field.field_key] = orderNum;
				orderNum++;
			}
		});
	}

	// Initialize field values on first render
	React.useEffect(() => {
		if (
			formData &&
			Object.keys(formData).length > 0 &&
			Object.keys(fieldValues).length === 0
		) {
			setFieldValues(formData);
		}
	}, [formData, fieldValues]);

	// Handle case where form has no fields (valid scenario)
	if (!submission?.form_fields || submission.form_fields.length === 0) {
		return (
			<div className='text-center py-8'>
				<FiFileText className='h-12 w-12 text-gray-300 mx-auto mb-3' />
				<p className='text-gray-500'>This form has no fields</p>
				<p className='text-sm text-gray-400 mt-1'>
					The submission was recorded but the form contained no input fields.
				</p>
			</div>
		);
	}

	// Handle missing data (shouldn't happen but good to handle)
	if (!submission?.data) {
		return <div className='text-gray-500'>No form responses available</div>;
	}

	// Only show error if formData is not a valid object (actual parse failure)
	// Empty objects are valid - fields will show "No response" individually
	if (formData === null || typeof formData !== 'object' || Array.isArray(formData)) {
		return <div className='text-red-600'>Error parsing form data</div>;
	}

	// Group fields by section
	const fieldsBySection = {};
	const fieldsWithoutSection = [];

	submission.form_fields.forEach((field) => {
		if (field.section_title) {
			if (!fieldsBySection[field.section_title]) {
				fieldsBySection[field.section_title] = [];
			}
			fieldsBySection[field.section_title].push(field);
		} else {
			fieldsWithoutSection.push(field);
		}
	});

	// Toggle edit mode
	const toggleEditMode = () => {
		if (isEditMode) {
			// Exiting edit mode - reset any unsaved changes
			setFieldValues(formData);
			setPendingChanges({});
			setEditingField(null);
		}
		setIsEditMode(!isEditMode);
		setError('');
	};

	// Start editing a specific field
	const startEditingField = (fieldKey) => {
		console.log('🔍 Starting edit for field:', fieldKey);
		console.log('📊 Current edit mode:', isEditMode);
		console.log('📝 Current editing field:', editingField);

		setEditingField(fieldKey);
		// Initialize with current value
		const currentValue = fieldValues[fieldKey] || formData[fieldKey] || '';
		setFieldValues((prev) => ({ ...prev, [fieldKey]: currentValue }));

		console.log(
			'✅ Started editing field:',
			fieldKey,
			'with value:',
			currentValue
		);
	};

	// Cancel editing a field
	const cancelEditingField = () => {
		setEditingField(null);
		// Reset field value to original
		if (editingField) {
			setFieldValues((prev) => ({
				...prev,
				[editingField]: formData[editingField] || '',
			}));
			// Remove from pending changes
			const newPending = { ...pendingChanges };
			delete newPending[editingField];
			setPendingChanges(newPending);
		}
	};

	// Update field value
	const updateFieldValue = (fieldKey, value) => {
		setFieldValues((prev) => ({ ...prev, [fieldKey]: value }));

		// Track as pending change if different from original
		const originalValue = formData[fieldKey];
		if (value !== originalValue) {
			setPendingChanges((prev) => ({ ...prev, [fieldKey]: value }));
		} else {
			// Remove from pending if same as original
			const newPending = { ...pendingChanges };
			delete newPending[fieldKey];
			setPendingChanges(newPending);
		}
	};

	// Save field changes
	const saveFieldChanges = async (fieldKey) => {
		if (
			!pendingChanges[fieldKey] &&
			fieldValues[fieldKey] === formData[fieldKey]
		) {
			setEditingField(null);
			return;
		}

		setSaving(true);
		setError('');

		try {
			const updatedData = { ...formData, [fieldKey]: fieldValues[fieldKey] };

			await SublyApi.request(
				`api/admin/forms/submissions/${submission.id}/data`,
				{ data: updatedData },
				'put',
				{ Authorization: `Bearer ${token}` }
			);

			// Update local data and notify parent
			if (onDataUpdate) {
				onDataUpdate(updatedData);
			}

			// Remove from pending changes
			const newPending = { ...pendingChanges };
			delete newPending[fieldKey];
			setPendingChanges(newPending);

			setEditingField(null);
		} catch (err) {
			console.error('❌ Error saving field:', err);
			setError(`Failed to save ${fieldKey}: ${err.message || 'Unknown error'}`);
		} finally {
			setSaving(false);
		}
	};

	// Save all pending changes
	const saveAllChanges = async () => {
		if (Object.keys(pendingChanges).length === 0) return;

		setSaving(true);
		setError('');

		try {
			const updatedData = { ...formData, ...pendingChanges };

			await SublyApi.request(
				`api/admin/forms/submissions/${submission.id}/data`,
				{ data: updatedData },
				'put',
				{ Authorization: `Bearer ${token}` }
			);

			// Update local data and notify parent
			if (onDataUpdate) {
				onDataUpdate(updatedData);
			}

			setPendingChanges({});
			setEditingField(null);
		} catch (err) {
			console.error('❌ Error saving all changes:', err);
			setError(`Failed to save changes: ${err.message || 'Unknown error'}`);
		} finally {
			setSaving(false);
		}
	};

	// Get icon for field type
	const getFieldIcon = (type) => {
		switch (type?.toLowerCase()) {
			case 'email':
				return <FiMail className='h-4 w-4 text-blue-500' />;
			case 'phone':
			case 'tel':
				return <FiPhone className='h-4 w-4 text-green-500' />;
			case 'date':
			case 'datetime':
				return <FiCalendar className='h-4 w-4 text-purple-500' />;
			case 'number':
				return <FiHash className='h-4 w-4 text-blue-600' />;
			case 'currency':
				return <FiDollarSign className='h-4 w-4 text-yellow-500' />;
			case 'url':
				return <FiLink className='h-4 w-4 text-indigo-500' />;
			case 'textarea':
				return <FiFileText className='h-4 w-4 text-gray-500' />;
			case 'file':
				return <FiFile className='h-4 w-4 text-orange-500' />;
			case 'image':
				return <FiImage className='h-4 w-4 text-pink-500' />;
			default:
				return <FiType className='h-4 w-4 text-gray-400' />;
		}
	};

	// Format field value based on type
	const formatFieldValue = (field, value) => {
		if (value === null || value === undefined || value === '') {
			return <span className='text-gray-400 italic'>No response</span>;
		}

		const fieldType = field.type?.toLowerCase();

		switch (fieldType) {
			case 'email':
				return (
					<a
						href={`mailto:${value}`}
						className='text-blue-600 hover:text-blue-800 underline'>
						{value}
					</a>
				);

			case 'phone':
			case 'tel':
				return (
					<a
						href={`tel:${value}`}
						className='text-blue-600 hover:text-blue-800 underline'>
						{value}
					</a>
				);

			case 'url':
				return (
					<a
						href={value}
						target='_blank'
						rel='noopener noreferrer'
						className='text-blue-600 hover:text-blue-800 underline'>
						{value}
					</a>
				);

			case 'date':
				try {
					// For date-only strings (YYYY-MM-DD), append T00:00:00 to
					// force local timezone interpretation and avoid off-by-one
					// errors from UTC midnight rolling back in US timezones
					const dateStr = /^\d{4}-\d{2}-\d{2}$/.test(value)
						? value + 'T00:00:00'
						: value;
					return new Date(dateStr).toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'long',
						day: 'numeric',
					});
				} catch {
					return value;
				}

			case 'time':
				// Format time value for display
				if (value) {
					try {
						// If it's already in HH:MM format, display as-is
						if (/^\d{2}:\d{2}$/.test(value)) {
							return value;
						}
						// Otherwise try to parse and format
						const timeDate = new Date(`1970-01-01T${value}`);
						return timeDate.toLocaleTimeString('en-US', {
							hour: '2-digit',
							minute: '2-digit',
						});
					} catch {
						return value;
					}
				}
				return value;

			case 'datetime':
				try {
					return new Date(value).toLocaleString('en-US', {
						year: 'numeric',
						month: 'long',
						day: 'numeric',
						hour: '2-digit',
						minute: '2-digit',
					});
				} catch {
					return value;
				}

			case 'currency':
			case 'number':
				if (!isNaN(value)) {
					return fieldType === 'currency'
						? new Intl.NumberFormat('en-US', {
								style: 'currency',
								currency: 'USD',
						  }).format(value)
						: Number(value).toLocaleString();
				}
				return value;

			case 'select':
			case 'radio': {
				// Resolve stored value (slug/UUID) to human-readable label
				const selectOptions = parseFieldOptions(field.options);
				return (
					<span className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800'>
						{resolveOptionLabel(selectOptions, value)}
					</span>
				);
			}

			case 'multiselect':
			case 'checkbox': {
				// Handle array of selected options - resolve values to labels
				const multiOptions = parseFieldOptions(field.options);
				if (Array.isArray(value)) {
					return (
						<div className='flex flex-wrap gap-1'>
							{value.map((item, index) => (
								<span
									key={index}
									className='inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800'>
									{resolveOptionLabel(multiOptions, item)}
								</span>
							))}
						</div>
					);
				}
				// Single value fallback - also resolve to label
				return resolveOptionLabel(multiOptions, value);
			}

			case 'boolean':
				return value ? (
					<span className='inline-flex items-center text-green-600'>
						<FiCheck className='h-4 w-4 mr-1' />
						Yes
					</span>
				) : (
					<span className='inline-flex items-center text-red-600'>
						<FiX className='h-4 w-4 mr-1' />
						No
					</span>
				);

			case 'file':
			case 'image':
				// Handle file uploads with smart previews (accept absolute or relative URLs)
				if (typeof value === 'string') {
					const { isImage, isPdf, fileType } = getFileTypeFromUrl(value);
					const fileIndex = fileFieldOrderMap[field.field_key] || 1;
					const filename = buildDownloadFilename(submission, formData, value, fileIndex);

					if (isImage) {
						return (
							<div className='w-full'>
								<div className='flex items-center space-x-3'>
									<img
										src={value}
										alt='Uploaded file'
										className='w-12 h-12 object-cover border border-gray-200 rounded flex-shrink-0'
										onError={(e) => {
											e.target.style.display = 'none';
											e.target.nextSibling.style.display = 'flex';
										}}
									/>
									<div
										style={{ display: 'none' }}
										className='flex items-center w-12 h-12 justify-center border border-gray-200 rounded bg-gray-50 flex-shrink-0'>
										<FiFile className='h-6 w-6 text-gray-500' />
									</div>
									<div className='min-w-0 flex-1'>
										<div className='text-sm font-medium text-gray-900 truncate'>
											Image File
										</div>
										<div className='flex items-center space-x-3 mt-1'>
											<a
												href={value}
												target='_blank'
												rel='noopener noreferrer'
												className='text-xs text-blue-600 hover:text-blue-800 underline'>
												View full size
											</a>
											<button
												type='button'
												onClick={() => handleDownload(value, filename)}
												className='inline-flex items-center text-xs text-gray-600 hover:text-gray-900 transition-colors'
												title='Download image'
												aria-label='Download image'>
												<FiDownload className='h-3.5 w-3.5 mr-1' />
												Download
											</button>
										</div>
									</div>
								</div>
							</div>
						);
					} else if (isPdf) {
						return (
							<div className='w-full'>
								<div className='flex items-center p-3 border border-gray-200 rounded bg-gray-50 w-full'>
									<FiFile className='h-8 w-8 text-red-500 mr-3 flex-shrink-0' />
									<div className='min-w-0 flex-1'>
										<div className='text-sm font-medium text-gray-900 truncate'>
											PDF Document
										</div>
										<div className='flex items-center space-x-3 mt-1'>
											<a
												href={value}
												target='_blank'
												rel='noopener noreferrer'
												className='text-xs text-blue-600 hover:text-blue-800 underline'>
												View PDF
											</a>
											<button
												type='button'
												onClick={() => handleDownload(value, filename)}
												className='inline-flex items-center text-xs text-gray-600 hover:text-gray-900 transition-colors'
												title='Download PDF'
												aria-label='Download PDF'>
												<FiDownload className='h-3.5 w-3.5 mr-1' />
												Download
											</button>
										</div>
									</div>
								</div>
							</div>
						);
					} else {
						return (
							<div className='w-full'>
								<div className='flex items-center p-3 border border-gray-200 rounded bg-gray-50 w-full'>
									<FiFile className='h-8 w-8 text-gray-500 mr-3 flex-shrink-0' />
									<div className='min-w-0 flex-1'>
										<div className='text-sm font-medium text-gray-900 truncate'>
											File uploaded
										</div>
										<div className='flex items-center space-x-3 mt-1'>
											<a
												href={value}
												target='_blank'
												rel='noopener noreferrer'
												className='text-xs text-blue-600 hover:text-blue-800 underline'>
												View file
											</a>
											<button
												type='button'
												onClick={() => handleDownload(value, filename)}
												className='inline-flex items-center text-xs text-gray-600 hover:text-gray-900 transition-colors'
												title='Download file'
												aria-label='Download file'>
												<FiDownload className='h-3.5 w-3.5 mr-1' />
												Download
											</button>
										</div>
									</div>
								</div>
							</div>
						);
					}
				}
				return value;

			case 'address':
				// Handle address objects
				if (typeof value === 'object' && value !== null) {
					const addressParts = [];
					if (value.street) addressParts.push(value.street);
					if (value.city) addressParts.push(value.city);
					if (value.state) addressParts.push(value.state);
					if (value.zip) addressParts.push(value.zip);

					const displayAddress =
						value.formatted_address || addressParts.join(', ');

					if (displayAddress) {
						return (
							<div className='flex items-start space-x-2'>
								<FiMapPin className='h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5' />
								<span className='text-gray-900 break-words'>
									{displayAddress}
								</span>
							</div>
						);
					}
				}
				// If it's a string (legacy format), display as-is
				if (typeof value === 'string' && value.trim()) {
					return (
						<div className='flex items-start space-x-2'>
							<FiMapPin className='h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5' />
							<span className='text-gray-900 break-words'>{value}</span>
						</div>
					);
				}

				return (
					<span className='text-gray-400 italic'>No address provided</span>
				);

			case 'heading':
				// Display element - show as formatted heading
				return (
					<div className='py-2'>
						<h3 className='text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2'>
							{field.label}
						</h3>
						{field.description && (
							<p className='text-sm text-gray-600 mt-2'>{field.description}</p>
						)}
					</div>
				);

			case 'paragraph':
				// Display element - show as formatted paragraph
				return (
					<div className='py-2'>
						<div className='text-gray-700'>
							{field.description || field.label}
						</div>
					</div>
				);

			case 'divider':
				// Display element - show as formatted divider
				return (
					<div className='py-4'>
						<hr className='border-gray-300' />
						{field.label && field.label !== 'Section Divider' && (
							<div className='text-center -mt-3'>
								<span className='bg-white px-4 text-sm text-gray-500'>
									{field.label}
								</span>
							</div>
						)}
					</div>
				);

			default:
				// Handle arrays that aren't already handled
				if (Array.isArray(value)) {
					return (
						<ul className='list-disc list-inside text-sm'>
							{value.map((item, index) => (
								<li key={index}>{String(item)}</li>
							))}
						</ul>
					);
				}
				// Handle objects that aren't already handled
				if (typeof value === 'object' && value !== null) {
					return (
						<div className='text-sm text-gray-600 bg-gray-50 p-2 rounded border'>
							<pre className='whitespace-pre-wrap text-xs'>
								{JSON.stringify(value, null, 2)}
							</pre>
						</div>
					);
				}
				return String(value);
		}
	};

	// Render field edit input based on type
	const renderFieldEditInput = (field) => {
		const fieldKey = field.field_key;
		const fieldType = field.type?.toLowerCase();
		const currentValue = fieldValues[fieldKey] || '';

		const options = parseFieldOptions(field.options);

		const inputProps = {
			value: currentValue,
			onChange: (e) => updateFieldValue(fieldKey, e.target.value),
			className:
				'w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent',
			disabled: saving,
		};

		switch (fieldType) {
			case 'email':
				return (
					<input
						type='email'
						{...inputProps}
						placeholder='Enter email address'
					/>
				);

			case 'phone':
			case 'tel':
				return (
					<input
						type='tel'
						{...inputProps}
						placeholder='Enter phone number (e.g., (555) 123-4567)'
						pattern='^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$'
						title='Please enter a valid phone number format: (555) 123-4567, 555-123-4567, or +1 555 123 4567'
						onInput={(e) => {
							const value = e.target.value;

							// Only allow digits, spaces, hyphens, parentheses, plus, and dots
							let cleanValue = value.replace(/[^0-9\s\-\(\)\+\.]/g, '');

							// Prevent more than one opening parenthesis
							const openParenCount = (cleanValue.match(/\(/g) || []).length;
							if (openParenCount > 1) {
								cleanValue = cleanValue.replace(/\(/g, '').replace(/^/, '(');
							}

							// Prevent more than one closing parenthesis
							const closeParenCount = (cleanValue.match(/\)/g) || []).length;
							if (closeParenCount > 1) {
								cleanValue = cleanValue
									.replace(/\)/g, '')
									.replace(/\(([0-9]{3})/, '($1)');
							}

							// Limit total length to 17 characters (longest valid format: +1 (555) 123-4567)
							cleanValue = cleanValue.slice(0, 17);

							// Don't allow closing paren without opening paren
							if (cleanValue.includes(')') && !cleanValue.includes('(')) {
								cleanValue = cleanValue.replace(/\)/g, '');
							}

							// Don't allow opening paren after digits have started (unless it's at the beginning)
							if (
								/[0-9].*\(/.test(cleanValue) &&
								!cleanValue.startsWith('+') &&
								!cleanValue.startsWith('1')
							) {
								cleanValue = cleanValue.replace(/\(/g, '');
							}

							if (value !== cleanValue) {
								e.target.value = cleanValue;
								updateFieldValue(fieldKey, cleanValue);
							}
						}}
					/>
				);

			case 'number':
				return (
					<input type='number' {...inputProps} placeholder='Enter number' />
				);

			case 'url':
				return <input type='url' {...inputProps} placeholder='Enter URL' />;

			case 'date':
				return <input type='date' {...inputProps} />;

			case 'time':
				return <input type='time' {...inputProps} />;

			case 'datetime':
				return <input type='datetime-local' {...inputProps} />;

			case 'textarea':
				return (
					<textarea {...inputProps} rows={3} placeholder='Enter text...' />
				);

			case 'select':
				if (options.length > 0) {
					return (
						<select {...inputProps}>
							<option value=''>Select an option</option>
							{options.map((option, index) => (
								<option key={index} value={option.value || option}>
									{option.label || option}
								</option>
							))}
						</select>
					);
				}
				return <input type='text' {...inputProps} placeholder='Enter value' />;

			case 'radio':
				if (options.length > 0) {
					return (
						<div className='space-y-2'>
							{options.map((option, index) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								return (
									<label key={index} className='flex items-center'>
										<input
											type='radio'
											name={fieldKey}
											value={optionValue}
											checked={currentValue === optionValue}
											onChange={(e) =>
												updateFieldValue(fieldKey, e.target.value)
											}
											className='mr-2'
											disabled={saving}
										/>
										{optionLabel}
									</label>
								);
							})}
						</div>
					);
				}
				return <input type='text' {...inputProps} placeholder='Enter value' />;

			case 'multiselect':
				if (options.length > 0) {
					const selectedValues = Array.isArray(currentValue)
						? currentValue
						: [];
					return (
						<div className='space-y-2 max-h-48 overflow-y-auto border border-gray-300 rounded-md p-3'>
							{options.map((option, index) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								return (
									<label key={index} className='flex items-center'>
										<input
											type='checkbox'
											checked={selectedValues.includes(optionValue)}
											onChange={(e) => {
												let newValues;
												if (e.target.checked) {
													newValues = [...selectedValues, optionValue];
												} else {
													newValues = selectedValues.filter(
														(v) => v !== optionValue
													);
												}
												updateFieldValue(fieldKey, newValues);
											}}
											className='mr-2'
											disabled={saving}
										/>
										{optionLabel}
									</label>
								);
							})}
						</div>
					);
				}
				return (
					<input
						type='text'
						{...inputProps}
						placeholder='Enter values (comma separated)'
					/>
				);

			case 'checkbox':
				if (options.length > 0) {
					const selectedValues = Array.isArray(currentValue)
						? currentValue
						: [];
					return (
						<div className='space-y-2'>
							{options.map((option, index) => {
								const optionValue = option.value || option;
								const optionLabel = option.label || option;
								return (
									<label key={index} className='flex items-center'>
										<input
											type='checkbox'
											checked={selectedValues.includes(optionValue)}
											onChange={(e) => {
												let newValues;
												if (e.target.checked) {
													newValues = [...selectedValues, optionValue];
												} else {
													newValues = selectedValues.filter(
														(v) => v !== optionValue
													);
												}
												updateFieldValue(fieldKey, newValues);
											}}
											className='mr-2'
											disabled={saving}
										/>
										{optionLabel}
									</label>
								);
							})}
						</div>
					);
				}
				return (
					<input
						type='text'
						{...inputProps}
						placeholder='Enter values (comma separated)'
					/>
				);

			case 'heading':
				// Display element - show as non-editable heading
				return (
					<div className='py-2'>
						<h3 className='text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2'>
							{field.label}
						</h3>
						{field.description && (
							<p className='text-sm text-gray-600 mt-2'>{field.description}</p>
						)}
						<p className='text-xs text-gray-500 mt-2 italic'>
							This is a display element and cannot be edited.
						</p>
					</div>
				);

			case 'paragraph':
				// Display element - show as non-editable paragraph
				return (
					<div className='py-2'>
						<div className='text-gray-700'>
							{field.description || field.label}
						</div>
						<p className='text-xs text-gray-500 mt-2 italic'>
							This is a display element and cannot be edited.
						</p>
					</div>
				);

			case 'divider':
				// Display element - show as non-editable divider
				return (
					<div className='py-4'>
						<hr className='border-gray-300' />
						{field.label && field.label !== 'Section Divider' && (
							<div className='text-center -mt-3'>
								<span className='bg-white px-4 text-sm text-gray-500'>
									{field.label}
								</span>
							</div>
						)}
						<p className='text-xs text-gray-500 mt-2 italic text-center'>
							This is a display element and cannot be edited.
						</p>
					</div>
				);

			case 'boolean':
				return (
					<select {...inputProps}>
						<option value=''>Select...</option>
						<option value='true'>Yes</option>
						<option value='false'>No</option>
					</select>
				);

			case 'file':
			case 'image':
				const renderCurrentFilePreview = () => {
					if (!currentValue || currentValue === 'Uploading...') return null;

					const { isImage, isPdf, fileType } = getFileTypeFromUrl(currentValue);

					return (
						<div className='flex items-center justify-between p-3 bg-gray-50 rounded border'>
							<div className='flex items-center space-x-3'>
								{isImage ? (
									<img
										src={currentValue}
										alt='Current file'
										className='w-12 h-12 object-cover border border-gray-200 rounded'
										onError={(e) => {
											e.target.style.display = 'none';
											e.target.nextSibling.style.display = 'flex';
										}}
									/>
								) : isPdf ? (
									<FiFile className='h-12 w-12 text-red-500' />
								) : (
									<FiFile className='h-12 w-12 text-gray-500' />
								)}
								<div
									style={{ display: 'none' }}
									className='flex items-center w-12 h-12 justify-center'>
									<FiFile className='h-8 w-8 text-gray-500' />
								</div>
								<div>
									<div className='text-sm font-medium text-gray-900'>
										{isPdf ? 'PDF Document' : isImage ? 'Image File' : 'File'}
									</div>
									<a
										href={currentValue}
										target='_blank'
										rel='noopener noreferrer'
										className='text-xs text-blue-600 hover:text-blue-800 underline'>
										{isPdf
											? 'View PDF'
											: isImage
											? 'View full size'
											: 'Download file'}
									</a>
								</div>
							</div>
							<Button
								size='sm'
								variant='ghost'
								onClick={async () => {
									const fileUrl = currentValue;

									// Only delete if it's a temporary file (permanent files will be cleaned up by the system)
									if (fileUrl && fileUrl.includes('temp-form-upload-')) {
										try {
											await SublyApi.request(
												'api/admin/forms/delete-file',
												{ fileUrl },
												'delete',
												{
													Authorization: `Bearer ${token}`,
													'Content-Type': 'application/json',
												}
											);
										} catch (err) {
											console.error('File deletion error:', err);
											setError(`Failed to delete file: ${err.message}`);
										}
									}

									// Clear the field value regardless of deletion success
									updateFieldValue(fieldKey, '');
								}}
								className='text-red-600 hover:text-red-800 h-8 w-8 p-0'
								title='Remove file'>
								<FiXCircle className='h-4 w-4' />
							</Button>
						</div>
					);
				};

				return (
					<div className='space-y-3'>
						{/* Current file preview */}
						{currentValue &&
							currentValue !== 'Uploading...' &&
							renderCurrentFilePreview()}

						{/* Upload status */}
						{currentValue === 'Uploading...' && (
							<div className='flex items-center p-3 bg-blue-50 rounded border border-blue-200'>
								<div className='animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full mr-3'></div>
								<span className='text-sm text-blue-700'>Uploading file...</span>
							</div>
						)}

						{/* File upload interface */}
						<div className='border border-dashed border-gray-300 rounded p-3 text-center hover:border-gray-400 transition-colors'>
							<div className='flex items-center justify-center space-x-2'>
								<FiFile className='h-4 w-4 text-gray-400' />
								<label
									htmlFor={`file-${fieldKey}`}
									className='cursor-pointer text-sm font-medium text-blue-600 hover:text-blue-500'>
									{currentValue ? 'Replace file' : 'Choose file'}
								</label>
							</div>
							<input
								id={`file-${fieldKey}`}
								type='file'
								className='sr-only'
								onChange={async (e) => {
									const file = e.target.files?.[0];
									if (file) {
										// Client-side file size validation
										const maxSize = 30 * 1024 * 1024; // 30MB
										if (file.size > maxSize) {
											setError(
												`File "${file.name}" is too large. Maximum size is 30MB.`
											);
											e.target.value = ''; // Clear the input
											return;
										}

										// Show loading state
										updateFieldValue(fieldKey, 'Uploading...');

										try {
											// Create FormData for upload
											const formData = new FormData();
											formData.append('file', file);

											// Upload to Vercel Blob via backend
											const response = await SublyApi.request(
												'api/admin/forms/upload-file',
												formData,
												'post',
												{
													Authorization: `Bearer ${token}`,
													'Content-Type': 'multipart/form-data',
												}
											);

											// Update field with file URL
											updateFieldValue(fieldKey, response.url);
										} catch (error) {
											console.error('Upload error:', error);
											updateFieldValue(fieldKey, formData[fieldKey] || '');
											setError(`Failed to upload file: ${error.message}`);
										}
									}
								}}
								accept={fieldType === 'image' ? 'image/*' : '*'}
								disabled={saving}
							/>
							<p className='text-xs text-gray-400 mt-1'>
								{fieldType === 'image' ? 'Images only, max 30MB' : 'Max 30MB'}
							</p>
						</div>
					</div>
				);

			case 'address':
				// For address fields, use MapboxPlacesAutocomplete for proper address selection
				return (
					<MapboxPlacesAutocomplete
						id={fieldKey}
						name={fieldKey}
						value={currentValue}
						onChange={(address) => {
							updateFieldValue(fieldKey, {
								street: address.street || '',
								city: address.city || '',
								state: address.state || '',
								zip: address.zip || '',
								coordinates: address.coordinates || null,
								formatted_address: address.formatted_address || '',
							});
						}}
						placeholder={field.placeholder || 'Enter an address'}
						required={field.is_required}
						disabled={saving}
						className='w-full'
					/>
				);

			default:
				return <input type='text' {...inputProps} placeholder='Enter value' />;
		}
	};

	// Render a field
	const renderField = (field) => {
		const fieldKey = field.field_key;
		const currentValue = fieldValues[fieldKey];
		const originalValue = formData[fieldKey];
		const fieldType = field.type?.toLowerCase();

		// Check if this is a display-only field
		const isDisplayField = ['heading', 'paragraph', 'divider'].includes(
			fieldType
		);

		// For display fields, render them directly without edit controls
		if (isDisplayField) {
			return (
				<div key={field.id} className='w-full'>
					{formatFieldValue(field, currentValue)}
				</div>
			);
		}

		// Improved isEmpty check that handles address objects
		const isEmpty = (() => {
			if (
				currentValue === null ||
				currentValue === undefined ||
				currentValue === ''
			) {
				return true;
			}

			// Special handling for address objects
			if (
				fieldType === 'address' &&
				typeof currentValue === 'object' &&
				currentValue !== null
			) {
				const { street, city, state, zip, formatted_address } = currentValue;
				// Address is empty if all components are empty
				return !street && !city && !state && !zip && !formatted_address;
			}

			// For arrays, check if empty
			if (Array.isArray(currentValue)) {
				return currentValue.length === 0;
			}

			return false;
		})();

		const hasChanges = pendingChanges[fieldKey] !== undefined;
		const isEditing = editingField === fieldKey;

		return (
			<div
				key={field.id}
				className={`
					p-4 rounded-lg border shadow-sm transition-colors
					h-full flex flex-col
					${
						hasChanges
							? 'bg-amber-50 border-amber-300'
							: isEmpty
							? 'bg-gray-50 border-gray-200'
							: 'bg-white border-gray-200'
					}
					${isEditMode && !hasChanges ? 'border-blue-300 bg-blue-50/30' : ''}
					${isEditing ? 'ring-2 ring-blue-500' : ''}
				`}>
				<div className='flex flex-col space-y-2 flex-grow'>
					{/* Field header with label and edit button */}
					<div className='flex items-center justify-between'>
						<div className='flex items-center space-x-2'>
							<label className='text-sm font-medium text-gray-900'>
								{field.label}
							</label>
							{field.required && (
								<span className='text-red-500 text-sm'>*</span>
							)}
							{hasChanges && (
								<span className='text-xs bg-amber-100 text-amber-800 px-2 py-1 rounded-full'>
									Modified
								</span>
							)}
							{isEditMode && !hasChanges && (
								<span className='text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full'>
									{isEditing ? 'Editing' : 'Editable'}
								</span>
							)}
						</div>
					</div>

					{/* Field type and edit button */}
					<div className='flex items-center justify-between'>
						<span className='text-xs text-gray-400 uppercase tracking-wider'>
							{field.type}
						</span>
						{isEditMode && !isEditing && (
							<Button
								size='sm'
								variant='ghost'
								onClick={() => startEditingField(fieldKey)}
								className='ml-2 h-8 w-8 p-1 border border-gray-300 hover:border-blue-500 hover:bg-blue-50'
								disabled={saving}
								title='Edit this field'>
								<FiEdit3 className='h-4 w-4 text-gray-600 hover:text-blue-600' />
							</Button>
						)}
					</div>
				</div>

				{/* Field description - only show for non-file fields or empty file fields */}
				{field.description && (
					<>
						{/* For file/image fields, only show description when empty */}
						{(fieldType === 'file' || fieldType === 'image') && isEmpty && (
							<p className='text-xs text-gray-500 mb-2'>{field.description}</p>
						)}

						{/* For address fields, only show description when empty or editing */}
						{fieldType === 'address' && (isEmpty || isEditing) && (
							<p className='text-xs text-gray-500 mb-2'>{field.description}</p>
						)}

						{/* For other fields, only show description when empty */}
						{!(
							fieldType === 'file' ||
							fieldType === 'image' ||
							fieldType === 'address'
						) &&
							isEmpty && (
								<p className='text-xs text-gray-500 mb-2'>
									{field.description}
								</p>
							)}
					</>
				)}

				{/* Field Value or Edit Input */}
				{isEditing ? (
					<div className='space-y-3'>
						{renderFieldEditInput(field)}

						{/* Edit Controls */}
						<div className='flex items-center justify-between'>
							<div className='text-xs text-gray-500'>
								{hasChanges ? 'Unsaved changes' : 'No changes'}
							</div>
							<div className='flex space-x-2'>
								<Button
									size='sm'
									variant='ghost'
									onClick={cancelEditingField}
									disabled={saving}
									className='text-gray-600 hover:text-gray-800'>
									<FiXCircle className='h-4 w-4 mr-1' />
									Cancel
								</Button>
								<Button
									size='sm'
									onClick={() => saveFieldChanges(fieldKey)}
									disabled={
										saving || (!hasChanges && currentValue === originalValue)
									}
									className='bg-blue-600 hover:bg-blue-700 text-white'>
									{saving ? (
										<>
											<div className='animate-spin h-3 w-3 border border-white border-t-transparent rounded-full mr-1'></div>
											Saving...
										</>
									) : (
										<>
											<FiSave className='h-4 w-4 mr-1' />
											Save
										</>
									)}
								</Button>
							</div>
						</div>
					</div>
				) : (
					<div className='text-gray-900'>
						{formatFieldValue(field, currentValue)}
					</div>
				)}

				{/* Required field warning */}
				{isEmpty && field.is_required && !isEditing && (
					<div className='mt-2 text-xs text-red-600 flex items-center'>
						<FiX className='h-3 w-3 mr-1' />
						Required field - no response provided
					</div>
				)}

				{/* Change indicator */}
				{hasChanges && !isEditing && (
					<div className='mt-2 text-xs text-amber-600 flex items-center'>
						<FiAlertCircle className='h-3 w-3 mr-1' />
						Modified - click Save All Changes to save
					</div>
				)}
			</div>
		);
	};

	// Render a section
	const renderSection = (sectionTitle, fields) => (
		<div key={sectionTitle} className='mb-8'>
			<div className='flex items-center mb-4'>
				<div className='flex-grow'>
					<h3 className='text-lg font-semibold text-gray-900'>
						{sectionTitle}
					</h3>
					<div className='h-0.5 bg-gray-200 mt-1'></div>
				</div>
				<span className='text-sm text-gray-500 ml-4'>
					{fields.length} field{fields.length !== 1 ? 's' : ''}
				</span>
			</div>
			<div className='grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch'>
				{fields.map(renderField)}
			</div>
		</div>
	);

	const totalFields = submission.form_fields.length;
	const answeredFields = submission.form_fields.filter((field) => {
		const value = formData[field.field_key];
		return value !== null && value !== undefined && value !== '';
	}).length;
	const completionRate =
		totalFields > 0 ? (answeredFields / totalFields) * 100 : 0;

	return (
		<div className='space-y-6'>
			{/* Edit Mode Controls */}
			<div className='flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-lg'>
				<div className='flex items-center space-x-4'>
					<Button
						onClick={toggleEditMode}
						variant={isEditMode ? 'primary' : 'outline'}
						className={isEditMode ? 'bg-blue-600 text-white' : ''}>
						<FiEdit3 className='h-4 w-4 mr-2' />
						{isEditMode ? 'Exit Edit Mode' : 'Edit Responses'}
					</Button>

					{isEditMode && Object.keys(pendingChanges).length > 0 && (
						<Button
							onClick={saveAllChanges}
							disabled={saving}
							className='bg-green-600 hover:bg-green-700 text-white'>
							{saving ? (
								<>
									<div className='animate-spin h-4 w-4 border border-white border-t-transparent rounded-full mr-2'></div>
									Saving All...
								</>
							) : (
								<>
									<FiSave className='h-4 w-4 mr-2' />
									Save All Changes ({Object.keys(pendingChanges).length})
								</>
							)}
						</Button>
					)}
				</div>

				{isEditMode && (
					<div className='text-sm text-gray-600'>
						{Object.keys(pendingChanges).length > 0
							? `${Object.keys(pendingChanges).length} unsaved change${
									Object.keys(pendingChanges).length !== 1 ? 's' : ''
							  }`
							: 'No pending changes'}
					</div>
				)}
			</div>

			{/* Error Message */}
			{error && (
				<div className='bg-red-50 border border-red-200 rounded-lg p-4'>
					<div className='flex items-center'>
						<FiAlertCircle className='h-5 w-5 text-red-600 mr-2' />
						<span className='text-red-800 font-medium'>Error</span>
					</div>
					<p className='text-red-700 mt-1'>{error}</p>
				</div>
			)}

			{/* Completion Summary */}
			<div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
				<div className='flex items-center justify-between mb-2'>
					<h3 className='text-sm font-medium text-blue-900'>
						Response Completion
					</h3>
					<span className='text-sm text-blue-700'>
						{answeredFields} of {totalFields} fields
					</span>
				</div>
				<div className='w-full bg-blue-200 rounded-full h-2'>
					<div
						className='bg-blue-600 h-2 rounded-full transition-all duration-300'
						style={{ width: `${completionRate}%` }}></div>
				</div>
				<p className='text-xs text-blue-700 mt-1'>
					{completionRate.toFixed(0)}% complete
				</p>
			</div>

			{/* Render sections */}
			{Object.entries(fieldsBySection).map(([sectionTitle, fields]) =>
				renderSection(sectionTitle, fields)
			)}

			{/* Render fields without sections */}
			{fieldsWithoutSection.length > 0 &&
				renderSection('Additional Information', fieldsWithoutSection)}

			{/* Files Section */}
			{standaloneFiles.length > 0 && (
				<div className='border-t pt-6'>
					<h3 className='text-lg font-semibold text-gray-900 mb-4'>
						Uploaded Files
					</h3>
					<div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
						{standaloneFiles.map((file, index) => {
							const filename = buildDownloadFilename(submission, formData, file.file_url, index + 1);
							return (
								<div
									key={file.id}
									className='border rounded-lg p-4 bg-white border-gray-300'>
									<div className='flex items-center justify-between mb-2'>
										<Label className='text-sm font-medium text-gray-700 flex items-center'>
											<FiFile className='h-4 w-4 text-orange-500 mr-2' />
											{file.field_key}
										</Label>
										<span className='text-xs text-gray-400'>
											{file.file_type}
										</span>
									</div>
									<div className='flex items-center space-x-3'>
										<a
											href={file.file_url}
											target='_blank'
											rel='noopener noreferrer'
											className='text-blue-600 hover:text-blue-800 underline text-sm'>
											{file.file_name}
										</a>
										<button
											type='button'
											onClick={() => handleDownload(file.file_url, filename)}
											className='inline-flex items-center text-xs text-gray-600 hover:text-gray-900 transition-colors'
											title='Download file'
											aria-label='Download file'>
											<FiDownload className='h-3.5 w-3.5 mr-1' />
											Download
										</button>
									</div>
									{file.file_size && (
										<p className='text-xs text-gray-500 mt-1'>
											{(file.file_size / 1024).toFixed(1)} KB
										</p>
									)}
								</div>
							);
						})}
					</div>
				</div>
			)}
		</div>
	);
};

export default FormResponsesDisplay;
export { buildDownloadFilename, getFilenameFromUrl, sanitize };
